<script lang="ts">
  import type { ModalStoryblok } from "$types/components";
  import Button from "$lib/atoms/Button.svelte";
  import { ButtonColor, IconType } from "$types/atoms";
  import { modalContent, modalIsOpened, modalStartIndex } from "$stores";
  import Image from "$lib/atoms/Image.svelte";
  import StoryblokComponent from "$lib/StoryblokComponent.svelte";
  import Slider from "$lib/molecules/Slider.svelte";
  import { fade } from "svelte/transition";
  import { expoOut } from "svelte/easing";
  import Video from "$lib/atoms/Video.svelte";
  import { setContext } from "svelte";

  export let blok: ModalStoryblok | ModalStoryblok[];

  $: blok0 = Array.isArray(blok) ? blok[0] : blok;
  $: bloks = Array.isArray(blok) ? blok : [blok];

  $: {
    setContext("isContainedModal", blok0?.containInScreen);
  }

  const close = () => {
    $modalContent = null;
    $modalIsOpened = false;
  };

  function slide(_node: HTMLElement) {
    return {
      duration: 800,
      easing: expoOut,
      css: (_t: number, u: number) => `transform: translateX(${u * 100}%)`,
    };
  }

  function onKeyDown(e: KeyboardEvent) {
    if (!blok0.allowKeyboardInput) {
      e.preventDefault();
    }
    if (e.key === "escape") {
      close();
    }
  }
</script>

<svelte:window on:keydown={onKeyDown} />

{#if blok0?.fadeIn}
  <div
    class="popup"
    class:pointer-events-none={!blok0?.allowKeyboardInput}
    in:fade|global={{ duration: 300 }}
    out:fade|global={{ duration: 300, delay: 100 }}
  >
    {#if blok0?.showCloseButton ?? true}
      <Button
        class="button-close pointer-events-auto"
        disableButtonHoverAnimation={true}
        color={ButtonColor.BLACK}
        icon={IconType.CLOSE}
        on:click={close}
        reduced
      />
    {/if}

    <div
      class="relative top-[3rem] h-[calc(100vh-140px)] flex flex-col"
      class:contained={blok0.containInScreen}
      class:rounded-lg={blok0.containInScreen}
    >
      {#if blok0?.content?.length}
        {#each blok0.content as content}
          <div class="flex-auto h-full pointer-events-auto">
            <StoryblokComponent blok={content} closeModal={close} />
          </div>
        {/each}
      {/if}
    </div>
  </div>
{:else}
  <div class="modal" transition:slide|global>
    {#if blok0?.showCloseButton !== false}
      <Button
        class="button-close"
        color={ButtonColor.BLACK}
        icon={IconType.CLOSE}
        on:click={close}
        reduced
      />
    {/if}
    <Slider
      class="modal-slider"
      initialSlide={$modalStartIndex}
      items={bloks}
      pagination={bloks.length > 1}
      slidesPerView={1}
      spaceBetween={20}
    >
      <div class="modal-inner" slot="slide" let:prop={modal}>
        <div class="header">
          <p class="type:section-title">{modal.eyebrow}</p>
        </div>
        <div class="thumbnail">
          {#if modal.thumbnail?.[0]}
            {#if modal.thumbnail[0].component === "video" && modal.thumbnail[0].media}
              <Video aspect={"16-9"} media={modal.thumbnail[0].media} />
            {:else if modal.thumbnail[0].component === "image" && modal.thumbnail[0].object}
              <Image
                aspect={"16-9"}
                lgSize="50"
                media={modal.thumbnail[0].object}
                smSize="100"
              />
            {/if}
          {/if}
        </div>

        <div class:content={modal.headline}>
          {#if modal.headline}
            <h3 class="headline type:h3">{modal.headline}</h3>
          {/if}

          {#if modal.content?.length}
            {#each modal.content as content}
              <div class="block">
                <StoryblokComponent blok={content} on:closeModal={close} />
              </div>
            {/each}
          {/if}
        </div>
      </div>
    </Slider>
  </div>
{/if}

<button
  class="overlay"
  in:fade|global={{ duration: 300 }}
  on:click={close}
  out:fade|global={{ duration: 300, delay: 100 }}
/>

<style lang="scss">
  .overlay {
    position: fixed;
    z-index: 999;
    height: 100%;
    width: 100%;
    top: 0;
    left: 0;
    background: rgba(#000, 0.75);
  }

  :global(.button.button-close) {
    position: absolute !important;
    top: fluid(22px, 22px);
    right: fluid(20px, 20px);
    z-index: 8;
  }

  :global(.modal-slider) {
    position: relative;
    z-index: 5;
  }

  .modal {
    position: fixed;
    right: 0;
    top: 0;
    height: 100vh;
    background: rgb(var(--color-light-grey));
    width: 100%;
    z-index: 1000;
    padding: fluid(40px, 32px) fluid(20px, 20px) 24px;
    overflow-y: auto;
    overflow-x: hidden;

    @include breakpoint("small") {
      width: fluid(864px, 600px);
      padding-bottom: fluid(55px);
    }
  }

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: fluid(32px, 32px);

    @include breakpoint("small") {
      align-items: flex-end;
    }
  }

  .headline {
    margin-bottom: 16px;
    @include breakpoint("small") {
      margin-bottom: 0;
    }
  }

  .thumbnail {
    margin-bottom: fluid(32px, 24px);
  }

  .content {
    @include breakpoint("small") {
      display: grid;
      grid-template-columns: 38% 58%;
      column-gap: 28px;
    }
  }

  .popup {
    position: fixed;
    right: 0;
    top: 0;
    height: 100vh;
    background: rgba(var(--color-light-grey), 0.95);
    width: 100%;
    z-index: 1000;
    padding: fluid(40px, 32px) fluid(20px, 20px) 24px;
    overflow-y: auto;
    overflow-x: hidden;
  }

  .contained {
    @include breakpoint("small") {
      :global(section.contained) {
        margin-top: 0;
        max-height: 100%;
      }
    }
  }
</style>
